// ─────────────────────────────────────────────────────────────────
// Glass-i — Post Purchase Bill to Accounts page (Stage 6P.6).
//
// Route:  /erp/purchase-accounting/post/:purchaseBillId
//
// Drives a single purchase bill through the Stage 6P.6 accounting-
// posting preview → post pipeline. The page is read-only on mount
// (calls purchaseAccountingApi.previewPosting) and submits via
// purchaseAccountingApi.postPosting; the backend is the source of
// truth for ledger resolution, debit/credit balance, round-off
// classification, ITC eligibility and duplicate-block decisions.
//
// Layout follows the Stage 6P.5 PostInventoryInwardPage shell:
//   * Bill identity header card  (Bill No / Date / Supplier / GSTIN /
//                                 Supplier Invoice / Match Status /
//                                 Grand Total + posting status badge)
//   * Ledger Mapping panel       (Supplier / Input CGST / Input SGST /
//                                 Input IGST / Round-Off /
//                                 per-item-type Purchase ledgers;
//                                 missing rows tagged red)
//   * Accounting Preview Table   (Line Type / Ledger / Debit / Credit /
//                                 Narration — colour-coded by line type)
//   * Summary card               (Total Debit / Total Credit /
//                                 Difference / Balanced flag /
//                                 blocker count)
//   * Blockers alert             (full list of reasons why canPost is
//                                 false — surfaces missing ledgers,
//                                 bill-status guards and existing
//                                 POSTED rows)
//   * Footer toolbar             (Post to Accounts disabled if
//                                 !canPost; Back)
//   * Result panel               (after a post — accounting voucher no
//                                 + posting_no, success/failure status,
//                                 posted_at)
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Empty,
  Result,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
  message
} from 'antd';
import {
  ArrowLeftOutlined,
  AuditOutlined,
  BankOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  FileDoneOutlined,
  ReloadOutlined,
  SendOutlined,
  WarningOutlined
} from '@ant-design/icons';

import { purchaseAccountingApi } from './purchaseAccountingApi';
import {
  accountingStatusColor,
  accountingStatusLabel,
  buildPurchaseAccountingPostPayload,
  calculateDebitCreditTotals,
  validateAccountingPreviewBalance
} from './purchaseAccountingHelpers';

const { Title, Text, Paragraph } = Typography;


// ─────────────────────────────────────────────────────────────────
// Tolerant numeric formatter — preview numbers arrive as JS numbers
// from the API. Zero collapses to a dim '—' so empty cells stay
// scannable in the per-row debit / credit columns.
// ─────────────────────────────────────────────────────────────────
function fmtMoney(v, decimals = 2) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (!Number.isFinite(n) || n === 0) return '—';
  return n.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
}

// Grand-total formatter — always show a value, including zero.
function fmtMoneyAlways(v, decimals = 2) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '0.00';
  return n.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
}

// Pretty-print ISO dates / timestamps. Backend may emit either a
// YYYY-MM-DD date or a full ISO timestamp; both render cleanly.
function fmtDate(v) {
  if (!v) return '—';
  const s = String(v);
  // YYYY-MM-DD prefix is fine on its own.
  if (s.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  return s;
}

function fmtDateTime(v) {
  if (!v) return '—';
  try {
    const d = new Date(v);
    if (Number.isFinite(d.getTime())) {
      return d.toLocaleString(undefined, {
        year:   'numeric',
        month:  '2-digit',
        day:    '2-digit',
        hour:   '2-digit',
        minute: '2-digit'
      });
    }
  } catch (_) { /* fall through */ }
  return String(v);
}


// ─────────────────────────────────────────────────────────────────
// Line-type palette — colour-codes the preview table by ledger role.
// Aligned with docs/23-antd-component-standards.md Tag colour
// guidance and the Stage 6P.5 PostInventoryInwardPage shell.
// ─────────────────────────────────────────────────────────────────
const LINE_TYPE_LABELS = {
  PURCHASE:         'Purchase',
  INPUT_CGST:       'Input CGST',
  INPUT_SGST:       'Input SGST',
  INPUT_IGST:       'Input IGST',
  SUPPLIER_PAYABLE: 'Supplier Payable',
  ROUND_OFF_GAIN:   'Round-Off Gain',
  ROUND_OFF_LOSS:   'Round-Off Loss'
};

const LINE_TYPE_COLORS = {
  PURCHASE:         'blue',
  INPUT_CGST:       'cyan',
  INPUT_SGST:       'cyan',
  INPUT_IGST:       'geekblue',
  SUPPLIER_PAYABLE: 'magenta',
  ROUND_OFF_GAIN:   'green',
  ROUND_OFF_LOSS:   'orange'
};

function lineTypeLabel(t) {
  if (!t) return '';
  const k = String(t).trim().toUpperCase();
  return LINE_TYPE_LABELS[k] || k;
}
function lineTypeColor(t) {
  if (!t) return 'default';
  const k = String(t).trim().toUpperCase();
  return LINE_TYPE_COLORS[k] || 'default';
}


// ─────────────────────────────────────────────────────────────────
// Bill match-status palette — mirrors GoodsReceiptsPage / Purchase
// Bills shell so the badge here matches the rest of the chrome.
// ─────────────────────────────────────────────────────────────────
const MATCH_STATUS_COLORS = {
  MATCHED:        'success',
  NOT_REQUIRED:   'default',
  PENDING:        'processing',
  REJECTED:       'error',
  UNMATCHED:      'warning'
};

function matchStatusColor(s) {
  if (!s) return 'default';
  const k = String(s).trim().toUpperCase();
  return MATCH_STATUS_COLORS[k] || 'default';
}


// ─────────────────────────────────────────────────────────────────
// Bill-status palette — kept narrow; only the values produced by the
// purchase-bill state machine appear here.
// ─────────────────────────────────────────────────────────────────
const BILL_STATUS_COLORS = {
  DRAFT:           'default',
  APPROVED:        'success',
  POSTED:          'success',
  POSTING_PENDING: 'processing',
  CANCELLED:       'default'
};

function billStatusColor(s) {
  if (!s) return 'default';
  const k = String(s).trim().toUpperCase();
  return BILL_STATUS_COLORS[k] || 'default';
}


// ─────────────────────────────────────────────────────────────────
// Ledger-mapping row builder — the preview response carries a
// `resolvedLedgers` shape of:
//   { supplier, cgst, sgst, igst, roundOff,
//     purchaseByItemType: [ { itemType, id, account_code, account_name } ] }
// Any of the singular slots may be `null` (when the resolver could
// not bind a ledger) and the `purchaseByItemType` array may be empty.
// We build a flat row list for the AntD Table; missing rows render
// with a red "Missing" tag so the operator can see the gap at a
// glance.
// ─────────────────────────────────────────────────────────────────
function buildLedgerMappingRows(resolvedLedgers) {
  const rl = resolvedLedgers && typeof resolvedLedgers === 'object'
    ? resolvedLedgers
    : {};
  const rows = [];

  // Singular slots — always render, even if missing, so the operator
  // sees the role and knows what to fix.
  const singulars = [
    { key: 'supplier', role: 'Supplier Payable',  ledger: rl.supplier },
    { key: 'cgst',     role: 'Input CGST',        ledger: rl.cgst     },
    { key: 'sgst',     role: 'Input SGST',        ledger: rl.sgst     },
    { key: 'igst',     role: 'Input IGST',        ledger: rl.igst     },
    { key: 'roundOff', role: 'Round-Off',         ledger: rl.roundOff }
  ];
  for (const s of singulars) {
    const l = s.ledger;
    rows.push({
      key:         `singular:${s.key}`,
      role:        s.role,
      itemType:    null,
      accountCode: l && l.account_code ? l.account_code : null,
      accountName: l && l.account_name ? l.account_name : null,
      missing:     !l || !l.id
    });
  }

  // Per-item-type Purchase ledgers.
  const byType = Array.isArray(rl.purchaseByItemType) ? rl.purchaseByItemType : [];
  if (byType.length === 0) {
    rows.push({
      key:         'purchase:none',
      role:        'Purchase',
      itemType:    null,
      accountCode: null,
      accountName: null,
      missing:     true
    });
  } else {
    for (const p of byType) {
      rows.push({
        key:         `purchase:${p.itemType || 'OTHER'}`,
        role:        'Purchase',
        itemType:    p.itemType || 'OTHER',
        accountCode: p.account_code || null,
        accountName: p.account_name || null,
        missing:     !p.id
      });
    }
  }

  return rows;
}


// ─────────────────────────────────────────────────────────────────
// Main page component
// ─────────────────────────────────────────────────────────────────
export default function PostPurchaseBillAccountingPage() {
  const { purchaseBillId } = useParams();
  const navigate = useNavigate();

  // Preview state.
  const [preview,    setPreview]    = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [loadError,  setLoadError]  = useState(null);

  // Post state — when set we render the AntD Result panel in place
  // of the action toolbar. `null` = no post attempted yet.
  const [postResult, setPostResult] = useState(null);
  const [postError,  setPostError]  = useState(null);
  const [posting,    setPosting]    = useState(false);


  // ── Initial preview fetch ────────────────────────────────────
  const loadPreview = useCallback(async () => {
    if (!purchaseBillId) {
      setLoadError('Missing purchase bill identifier in URL.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const data = await purchaseAccountingApi.previewPosting(purchaseBillId);
      setPreview(data || null);
    } catch (e) {
      const msg = (e && e.response && e.response.data &&
                    (e.response.data.message || e.response.data.code))
        || (e && e.message)
        || 'Failed to load accounting posting preview.';
      setLoadError(msg);
      setPreview(null);
    } finally {
      setLoading(false);
    }
  }, [purchaseBillId]);

  useEffect(() => {
    setPostResult(null);
    setPostError(null);
    loadPreview();
  }, [loadPreview]);


  // ── Submit handler ───────────────────────────────────────────
  const handlePost = useCallback(async () => {
    if (!purchaseBillId) return;
    setPosting(true);
    setPostError(null);
    try {
      const payload = buildPurchaseAccountingPostPayload({});
      const result  = await purchaseAccountingApi.postPosting(purchaseBillId, payload);

      // Two success shapes from the service:
      //   * happy path: { posting, voucher, itcRowIds }
      //   * voucher-engine failure: { posting (status POSTING_FAILED), error }
      const posting   = (result && result.posting)   || null;
      const voucher   = (result && result.voucher)   || null;
      const errorBlob = (result && result.error)     || null;

      // Backend may emit either `status` (mapper) or `postingStatus`
      // (camel-normalised alias). Probe both.
      const status =
        (posting && (posting.status || posting.postingStatus)) ||
        (errorBlob ? 'POSTING_FAILED' : 'POSTED');

      const postingNo =
        (posting && (posting.postingNo || posting.posting_no)) || null;

      const voucherNo =
        (voucher && (voucher.voucherNo || voucher.voucher_no)) ||
        (posting && (posting.voucherNo || posting.voucher_no ||
                     posting.accountingVoucherNo || posting.accounting_voucher_no)) ||
        null;

      const postedAt =
        (posting && (posting.postedAt || posting.posted_at)) || null;

      setPostResult({
        success:   !errorBlob && String(status).toUpperCase() === 'POSTED',
        status,
        postingNo,
        voucherNo,
        postedAt,
        errorMessage: errorBlob ? (errorBlob.message || errorBlob.code) : null,
        posting,
        voucher
      });

      if (!errorBlob && String(status).toUpperCase() === 'POSTED') {
        message.success(
          `Posted ${postingNo || 'voucher'} → ${voucherNo || 'accounting voucher'}.`
        );
      } else {
        message.warning(
          errorBlob && errorBlob.message
            ? `Posting failed: ${errorBlob.message}`
            : 'Posting did not complete successfully.'
        );
      }
    } catch (e) {
      const msg = (e && e.response && e.response.data &&
                    (e.response.data.message || e.response.data.code))
        || (e && e.message)
        || 'Failed to post the purchase bill to accounts.';
      setPostError(msg);
      message.error(msg);
    } finally {
      setPosting(false);
    }
  }, [purchaseBillId]);


  // ── Derived data ─────────────────────────────────────────────
  const bill            = preview && preview.bill ? preview.bill : null;
  const lines           = preview && Array.isArray(preview.lines) ? preview.lines : [];
  const resolvedLedgers = preview ? preview.resolvedLedgers : null;
  const blockers        = preview && Array.isArray(preview.blockers) ? preview.blockers : [];
  const existing        = preview && preview.existingPosting ? preview.existingPosting : null;

  // Defensive totals — server provides `totals` but we recompute
  // client-side so the footer stays in sync if the table is
  // re-rendered with a stale preview snapshot.
  const totals = useMemo(() => {
    const server = (preview && preview.totals) || null;
    if (server &&
        typeof server.totalDebit === 'number' &&
        typeof server.totalCredit === 'number' &&
        typeof server.difference === 'number') {
      return {
        totalDebit:  server.totalDebit,
        totalCredit: server.totalCredit,
        difference:  server.difference,
        balanced:    Boolean(server.balanced)
      };
    }
    const local = calculateDebitCreditTotals(lines);
    return {
      totalDebit:  local.totalDebit,
      totalCredit: local.totalCredit,
      difference:  local.difference,
      balanced:    validateAccountingPreviewBalance(lines)
    };
  }, [preview, lines]);

  const ledgerRows = useMemo(
    () => buildLedgerMappingRows(resolvedLedgers),
    [resolvedLedgers]
  );

  // Server-authoritative "can post" gate. Posting also blocked once
  // a result is present so the operator can't double-submit.
  const canPost = Boolean(
    preview && preview.canPost &&
    !posting && !postResult
  );


  // ── Preview table columns ────────────────────────────────────
  const previewColumns = useMemo(() => [
    {
      title: '#',
      width: 50,
      align: 'center',
      render: (_, row, idx) => row.lineNo || idx + 1
    },
    {
      title: 'Line Type',
      dataIndex: 'postingLineType',
      width: 170,
      render: (v) => (
        <Tag color={lineTypeColor(v)} style={{ marginInlineEnd: 0 }}>
          {lineTypeLabel(v)}
        </Tag>
      )
    },
    {
      title: 'Ledger',
      dataIndex: 'ledgerAccountName',
      render: (_, row) => {
        if (!row.ledgerAccountId && !row.ledgerAccountCode) {
          return (
            <Tag color="error" icon={<WarningOutlined />}>
              Unresolved
            </Tag>
          );
        }
        return (
          <Space direction="vertical" size={0}>
            <span style={{ fontWeight: 500 }}>
              {row.ledgerAccountName || '(unnamed)'}
            </span>
            {row.ledgerAccountCode ? (
              <Text type="secondary" style={{ fontSize: 11 }}>
                {row.ledgerAccountCode}
                {row.itemType ? ` · ${row.itemType}` : ''}
              </Text>
            ) : null}
          </Space>
        );
      }
    },
    {
      title: 'Debit',
      dataIndex: 'debitAmount',
      width: 140,
      align: 'right',
      render: (v) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {fmtMoney(v, 2)}
        </span>
      )
    },
    {
      title: 'Credit',
      dataIndex: 'creditAmount',
      width: 140,
      align: 'right',
      render: (v) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {fmtMoney(v, 2)}
        </span>
      )
    },
    {
      title: 'Narration',
      dataIndex: 'narration',
      ellipsis: true,
      render: (v) => v
        ? <span style={{ color: '#475569' }}>{v}</span>
        : <span style={{ color: '#cbd5e1' }}>—</span>
    }
  ], []);

  // ── Row class — visually highlight the credit / round-off legs.
  const previewRowClassName = useCallback((row) => {
    const t = row && row.postingLineType
      ? String(row.postingLineType).toUpperCase() : '';
    if (t === 'SUPPLIER_PAYABLE')  return 'pa-line--credit';
    if (t === 'ROUND_OFF_GAIN')    return 'pa-line--gain';
    if (t === 'ROUND_OFF_LOSS')    return 'pa-line--loss';
    return '';
  }, []);


  // ── Ledger mapping columns ───────────────────────────────────
  const ledgerColumns = useMemo(() => [
    {
      title: 'Role',
      dataIndex: 'role',
      width: 200,
      render: (v, row) => (
        <Space direction="vertical" size={0}>
          <span style={{ fontWeight: 500 }}>{v}</span>
          {row.itemType ? (
            <Tag color="blue" style={{ marginInlineEnd: 0 }}>
              {row.itemType}
            </Tag>
          ) : null}
        </Space>
      )
    },
    {
      title: 'Account Code',
      dataIndex: 'accountCode',
      width: 200,
      render: (v) => v
        ? <Text code>{v}</Text>
        : <Tag color="error" icon={<WarningOutlined />}>Missing</Tag>
    },
    {
      title: 'Account Name',
      dataIndex: 'accountName',
      render: (v, row) => v
        ? <span>{v}</span>
        : (
          <Text type="danger">
            No ledger bound for this role
            {row.itemType ? ` (${row.itemType})` : ''}.
          </Text>
        )
    }
  ], []);

  const ledgerRowClassName = useCallback((row) =>
    row && row.missing ? 'pa-ledger-row--missing' : '', []);


  // ── Render ───────────────────────────────────────────────────
  return (
    <div className="p-4">
      {/* Page head */}
      <div className="flex items-start justify-between mb-3">
        <div>
          <Title level={4} style={{ marginBottom: 0 }}>
            <AuditOutlined style={{ marginInlineEnd: 8 }} />
            Post Purchase Bill to Accounts
          </Title>
          <Text type="secondary">
            Review resolved ledgers and the candidate accounting voucher.
            Backend recomputes on post and is the final authority.
          </Text>
        </div>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={loadPreview}
            disabled={loading || posting}
          >
            Refresh
          </Button>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
        </Space>
      </div>

      {/* Load error */}
      {loadError ? (
        <Alert
          type="error"
          showIcon
          message="Could not load posting preview"
          description={loadError}
          style={{ marginBottom: 12 }}
          action={
            <Button size="small" onClick={loadPreview}>
              Retry
            </Button>
          }
        />
      ) : null}

      <Spin spinning={loading}>
        {/* Bill identity header */}
        {bill ? (
          <Card
            size="small"
            style={{ marginBottom: 12 }}
            title={
              <Space wrap>
                <FileDoneOutlined />
                <span>Bill Identity</span>
                {bill.status ? (
                  <Tag color={billStatusColor(bill.status)}>
                    {bill.status}
                  </Tag>
                ) : null}
                {bill.matchStatus ? (
                  <Tag color={matchStatusColor(bill.matchStatus)}>
                    Match: {bill.matchStatus}
                  </Tag>
                ) : null}
                {existing && existing.postingStatus ? (
                  <Tag color={accountingStatusColor(existing.postingStatus)}>
                    Existing: {accountingStatusLabel(existing.postingStatus)}
                  </Tag>
                ) : null}
              </Space>
            }
          >
            <Descriptions
              size="small"
              column={{ xs: 1, sm: 2, md: 3, lg: 4 }}
              colon
            >
              <Descriptions.Item label="Bill No">
                {bill.billNo || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Bill Date">
                {fmtDate(bill.billDate)}
              </Descriptions.Item>
              <Descriptions.Item label="Supplier">
                {bill.supplierName || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Supplier GSTIN">
                {bill.supplierGstin
                  ? <Text code>{bill.supplierGstin}</Text>
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Supplier Invoice No">
                {bill.supplierInvoiceNo || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Supplier Invoice Date">
                {fmtDate(bill.supplierInvoiceDate)}
              </Descriptions.Item>
              <Descriptions.Item label="Grand Total">
                <Text strong style={{ fontVariantNumeric: 'tabular-nums' }}>
                  ₹ {fmtMoneyAlways(bill.grandTotal)}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Total Tax">
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                  ₹ {fmtMoneyAlways(bill.totalTax)}
                </span>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        ) : null}

        {/* Blockers banner — surfaces the reasons canPost is false */}
        {blockers.length > 0 ? (
          <Alert
            type={preview && preview.canPost ? 'warning' : 'error'}
            showIcon
            icon={<ExclamationCircleOutlined />}
            message={
              preview && preview.canPost
                ? `${blockers.length} warning${blockers.length === 1 ? '' : 's'} on this preview`
                : `Posting blocked — ${blockers.length} issue${blockers.length === 1 ? '' : 's'} must be resolved`
            }
            description={
              <ul style={{ margin: 0, paddingInlineStart: 18 }}>
                {blockers.map((b, idx) => (
                  <li key={idx}>
                    {b && b.code ? (
                      <Text code style={{ marginInlineEnd: 6 }}>{b.code}</Text>
                    ) : null}
                    <span>{(b && b.message) || 'Unknown issue.'}</span>
                  </li>
                ))}
              </ul>
            }
            style={{ marginBottom: 12 }}
          />
        ) : null}

        {/* Existing-posting cross-link */}
        {existing && existing.id ? (
          <Alert
            type="info"
            showIcon
            message={
              <Space wrap>
                <span>An accounting posting already exists for this bill:</span>
                {existing.postingNo ? (
                  <Tag color="processing">{existing.postingNo}</Tag>
                ) : null}
                {existing.accountingVoucherNo ? (
                  <Tag color="blue">{existing.accountingVoucherNo}</Tag>
                ) : null}
                {existing.postingStatus ? (
                  <Tag color={accountingStatusColor(existing.postingStatus)}>
                    {accountingStatusLabel(existing.postingStatus)}
                  </Tag>
                ) : null}
              </Space>
            }
            style={{ marginBottom: 12 }}
          />
        ) : null}

        {/* Ledger Mapping panel */}
        <Card
          size="small"
          title={
            <Space>
              <BankOutlined />
              <span>Ledger Mapping</span>
              <Text type="secondary">
                ({ledgerRows.length} role{ledgerRows.length === 1 ? '' : 's'})
              </Text>
            </Space>
          }
          style={{ marginBottom: 12 }}
        >
          {ledgerRows.length > 0 ? (
            <Table
              size="small"
              rowKey="key"
              columns={ledgerColumns}
              dataSource={ledgerRows}
              pagination={false}
              rowClassName={ledgerRowClassName}
              locale={{ emptyText: ' ' }}
            />
          ) : (
            <Empty description="No ledger mapping available." />
          )}
        </Card>

        {/* Accounting Preview Table */}
        <Card
          size="small"
          title={
            <Space>
              <span>Accounting Preview</span>
              <Text type="secondary">
                ({lines.length} line{lines.length === 1 ? '' : 's'})
              </Text>
            </Space>
          }
          style={{ marginBottom: 12 }}
        >
          <Table
            size="small"
            rowKey={(row, idx) =>
              (row && (row.lineNo !== undefined && row.lineNo !== null)
                ? `line-${row.lineNo}-${row.postingLineType || idx}`
                : `line-${idx}`)
            }
            columns={previewColumns}
            dataSource={lines}
            pagination={false}
            rowClassName={previewRowClassName}
            scroll={{ x: 900 }}
            locale={{ emptyText: loading ? ' ' : 'No accounting lines to preview.' }}
          />
        </Card>

        {/* Summary card */}
        <Card size="small" style={{ marginBottom: 12 }}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Statistic
              title="Total Debit"
              value={fmtMoneyAlways(totals.totalDebit)}
              prefix="₹"
              valueStyle={{ fontVariantNumeric: 'tabular-nums' }}
            />
            <Statistic
              title="Total Credit"
              value={fmtMoneyAlways(totals.totalCredit)}
              prefix="₹"
              valueStyle={{ fontVariantNumeric: 'tabular-nums' }}
            />
            <Statistic
              title="Difference"
              value={fmtMoneyAlways(totals.difference)}
              prefix="₹"
              valueStyle={{
                color: Math.abs(Number(totals.difference) || 0) <= 0.01
                  ? '#16a34a'
                  : '#dc2626',
                fontVariantNumeric: 'tabular-nums'
              }}
            />
            <div className="flex items-center">
              {totals.balanced ? (
                <Tag
                  color="success"
                  icon={<CheckCircleOutlined />}
                  style={{ fontSize: 13, padding: '4px 10px' }}
                >
                  Balanced
                </Tag>
              ) : (
                <Tag
                  color="error"
                  icon={<CloseCircleOutlined />}
                  style={{ fontSize: 13, padding: '4px 10px' }}
                >
                  Unbalanced
                </Tag>
              )}
            </div>
          </div>
        </Card>

        {/* Post result (replaces toolbar after a successful post) */}
        {postResult ? (
          <Card size="small" style={{ marginBottom: 12 }}>
            <Result
              status={postResult.success ? 'success' : 'error'}
              title={
                postResult.success
                  ? `Posted as ${postResult.voucherNo || '(accounting voucher)'}`
                  : 'Posting did not complete'
              }
              subTitle={
                <Space direction="vertical" size={4}>
                  <span>
                    Posting No:&nbsp;
                    <strong>{postResult.postingNo || '—'}</strong>
                    {' · '}
                    Voucher No:&nbsp;
                    <strong>{postResult.voucherNo || '—'}</strong>
                  </span>
                  <span>
                    Status:&nbsp;
                    <Tag color={accountingStatusColor(postResult.status)}>
                      {accountingStatusLabel(postResult.status)}
                    </Tag>
                  </span>
                  {postResult.postedAt ? (
                    <span>
                      Posted At:&nbsp;
                      <strong>{fmtDateTime(postResult.postedAt)}</strong>
                    </span>
                  ) : null}
                  {postResult.errorMessage ? (
                    <Paragraph type="danger" style={{ marginBottom: 0 }}>
                      {postResult.errorMessage}
                    </Paragraph>
                  ) : null}
                </Space>
              }
              extra={[
                <Button
                  key="back"
                  icon={<ArrowLeftOutlined />}
                  onClick={() => navigate(-1)}
                >
                  Back
                </Button>,
                <Button
                  key="refresh"
                  type="primary"
                  onClick={() => {
                    setPostResult(null);
                    setPostError(null);
                    loadPreview();
                  }}
                >
                  Refresh Preview
                </Button>
              ]}
            />
          </Card>
        ) : null}

        {/* Post error (kept above the toolbar so the operator sees it) */}
        {postError && !postResult ? (
          <Alert
            type="error"
            showIcon
            message="Posting failed"
            description={postError}
            style={{ marginBottom: 12 }}
            closable
            onClose={() => setPostError(null)}
          />
        ) : null}

        {/* Footer toolbar — hidden once we have a post result */}
        {!postResult ? (
          <div className="flex items-center justify-end gap-2">
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(-1)}
              disabled={posting}
            >
              Back
            </Button>
            <Tooltip
              title={
                canPost
                  ? ''
                  : !preview
                    ? 'Loading preview…'
                    : !totals.balanced
                      ? 'Voucher is unbalanced — cannot post.'
                      : blockers.length > 0
                        ? 'Resolve the blockers listed above before posting.'
                        : existing && (
                            String(existing.postingStatus || '').toUpperCase() === 'POSTED' ||
                            String(existing.postingStatus || '').toUpperCase() === 'POSTING_READY'
                          )
                          ? 'A posting already exists for this bill.'
                          : posting
                            ? 'Posting in progress…'
                            : 'This bill is not ready for accounting posting.'
              }
            >
              <Button
                type="primary"
                icon={<SendOutlined />}
                loading={posting}
                disabled={!canPost}
                onClick={handlePost}
              >
                Post to Accounts
              </Button>
            </Tooltip>
          </div>
        ) : null}
      </Spin>

      {/* Lightweight inline styles for row colouring — avoids a
          companion .css file while keeping the visual cue obvious.
          The slate-50 / red-50 tints match the Stage 6P.5 preview
          page so the two posting surfaces feel like a set. */}
      <style>{`
        .pa-line--credit > td { background-color: #fdf4ff !important; }
        .pa-line--credit:hover > td { background-color: #fae8ff !important; }
        .pa-line--gain   > td { background-color: #f0fdf4 !important; }
        .pa-line--gain:hover   > td { background-color: #dcfce7 !important; }
        .pa-line--loss   > td { background-color: #fff7ed !important; }
        .pa-line--loss:hover   > td { background-color: #ffedd5 !important; }
        .pa-ledger-row--missing > td { background-color: #fef2f2 !important; }
        .pa-ledger-row--missing:hover > td { background-color: #fee2e2 !important; }
      `}</style>
    </div>
  );
}
